
import Vue from "vue";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import { ControlsTreeContext, DropdownOptions } from "@/types/controls";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import DatePickerInput from "@/components/Reusable/CustomInputs/DatePickerInput.vue";
import MultiselectInput from "@/components/Reusable/CustomInputs/MultiselectInput.vue";
import TimePickerInput from "@/components/Reusable/CustomInputs/TimePickerInput.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";

type Field = {
  type: string;
  label: string;
  value: string;
  multiple?: boolean;
  options: { label: string; value: string }[];
  advanced: { id: string };
};

type ConditionalValue = {
  field: Field | string | null;
  operand: string | null;
  value: string | string[] | null;
};

const DEFAULT_VALUE = {
  field: null,
  operand: null,
  value: null
};

@Component({
  components: {
    AlertBox,
    TimePickerInput,
    MultiselectInput,
    DatePickerInput,
    DropdownInput,
    TextInput
  }
})
export default class ConditionalFieldInput extends Vue {
  @Inject("context") readonly context!: ControlsTreeContext;

  @Prop()
  value!: ConditionalValue | null;

  @Prop()
  dropdownOptions!: DropdownOptions;

  @Prop()
  fieldId!: string | null; // The field this conditional is attached to.

  internalValue: ConditionalValue = this.value || DEFAULT_VALUE;

  @Watch("value")
  updateInternalValue() {
    this.internalValue = this.value || DEFAULT_VALUE;
  }

  get fieldToCompareId() {
    if (typeof this.internalValue?.field === "string") {
      return this.internalValue.field;
    }

    // @deprecated - this is for backwards compatibility, field is now a string for new conditionals.
    return this.internalValue?.field?.advanced?.id;
  }

  get valueAsArray() {
    return Array.isArray(this.internalValue?.value)
      ? this.internalValue.value
      : [];
  }

  get isValueToCompareSet() {
    return this.internalValue.value?.length;
  }

  get operandOptions() {
    if (this.field === null) return [];

    if (this.field?.type === "file") {
      return [
        { text: "is set", value: "is set" },
        { text: "is not set", value: "is not set" }
      ];
    }

    const options = [
      { text: "equals", value: "equals" },
      { text: "not equals", value: "not equals" },
      { text: "is set", value: "is set" },
      { text: "is not set", value: "is not set" }
    ];

    if (this.isArrayLikeValue) {
      options.push(
        { text: "is one of", value: "is one of" },
        { text: "is none of", value: "is none of" },
        { text: "contains", value: "contains" },
        {
          text: "does not contain",
          value: "does not contain"
        }
      );
    }

    if (this.isDate) {
      options.push(
        { text: "is before", value: "is before date" },
        { text: "is after", value: "is after date" }
      );
    }

    if (this.isTime) {
      options.push(
        { text: "is before", value: "is before time" },
        { text: "is after", value: "is after time" }
      );
    }

    if (this.isNumber) {
      options.push(
        { text: "is greater than", value: "is greater than" },
        { text: "is less than", value: "is less than" }
      );
    }

    return options;
  }

  get multiSelectOperand() {
    return (
      this.value?.operand === "is one of" ||
      this.value?.operand === "is none of"
    );
  }

  editField(field: Field) {
    this.internalValue.field = field?.advanced?.id;
    this.internalValue.value = DEFAULT_VALUE.value;
    this.internalValue.operand = DEFAULT_VALUE.operand;

    this.$emit("input", this.internalValue);
  }

  editOperand(operand: string) {
    this.internalValue.operand = operand;

    this.$emit("input", this.internalValue);
  }

  editValue(value: string | string[]) {
    this.internalValue.value = value;

    this.$emit("input", this.internalValue);
  }

  get field() {
    const context = this.context.getControlValue("content.form.fields");
    // Grab a fresh copy of the fields, not a stale one.
    const fields = (context as Field[]) || null;
    return fields?.find(field => field?.advanced?.id === this.fieldToCompareId);
  }

  get internalDropdownOptions() {
    if (!this.dropdownOptions) {
      return this.dropdownOptions;
    }

    const options = this.dropdownOptions;

    if (options.populate) {
      options.populate.condition = {
        path: "advanced.id",
        operand: "not equals",
        value: this.fieldId
      };
    }

    return options;
  }

  get availableOptions() {
    return this.field?.options?.map(option => {
      const isNullish =
        option.value === null ||
        option.value === undefined ||
        option.value === "";

      return {
        text: option.label,
        value: isNullish ? option.label : option.value
      };
    });
  }

  get selectedOption() {
    const selectedOptions = Array.isArray(this.internalValue.value)
      ? this.internalValue.value
      : [this.internalValue.value];

    return selectedOptions.some(option =>
      this.availableOptions?.some(fieldOption => fieldOption.value === option)
    );
  }

  get hasOptions() {
    return !!this.field?.options;
  }

  get isNumber() {
    return this.field?.type === "number";
  }

  get isDate() {
    return this.field?.type === "date";
  }

  get isTime() {
    return this.field?.type === "time";
  }

  get isString() {
    return (
      !this.isDate &&
      !this.isTime &&
      !this.hasOptions &&
      !this.isOperandSetOrNot
    );
  }

  get isArrayLikeValue() {
    return (
      this.isString || this.field?.type === "checkbox" || this.field?.multiple
    );
  }

  get isOperandSetOrNot() {
    return (
      this.internalValue.operand === "is set" ||
      this.internalValue.operand === "is not set"
    );
  }
}
